import { ENCDE_SECRET } from "store/api/constApi";

const LEGACY_PADDING_CHARACTER = "i";
const LEGACY_ALPHABET_LENGTH = 64;
const MAX_LEGACY_VALUE_LENGTH = 128;

export const isLegacyClusterPath = (
  clusterId: string | undefined
): boolean => Boolean(clusterId && !clusterId.startsWith("ci_"));

export const isLegacyClusterRootPath = (
  pathname: string,
  clusterId: string | undefined
): boolean => {
  if (!isLegacyClusterPath(clusterId)) {
    return false;
  }

  const pathSegments = pathname.split("/").filter(Boolean);
  return pathSegments.length === 1 && pathSegments[0] === clusterId;
};

const getLegacyAlphabet = (codes: string): string | null => {
  if (codes.length < LEGACY_ALPHABET_LENGTH) return null;

  const alphabet = codes.slice(0, LEGACY_ALPHABET_LENGTH);
  if (alphabet.includes(LEGACY_PADDING_CHARACTER)) {
    return null;
  }

  return alphabet;
};

/**
 * Decodes values generated by the legacy PHP encode_decode class. Its `codes`
 * value is a custom Base64 alphabet and `i` is used instead of `=` padding.
 */
export const decodeLegacyUrlValue = (
  encodedValue: string | undefined,
  codes: string = ENCDE_SECRET,
  byteAliases?: ReadonlyMap<number, number>
): string | null => {
  const alphabet = getLegacyAlphabet(codes);
  if (
    !alphabet ||
    !encodedValue ||
    encodedValue.length > MAX_LEGACY_VALUE_LENGTH ||
    encodedValue.length % 4 !== 0
  ) {
    return null;
  }

  const paddingStart = encodedValue.indexOf(LEGACY_PADDING_CHARACTER);
  const paddingLength =
    paddingStart === -1 ? 0 : encodedValue.length - paddingStart;

  if (
    paddingLength > 2 ||
    (paddingStart !== -1 &&
      encodedValue.slice(paddingStart) !==
        LEGACY_PADDING_CHARACTER.repeat(paddingLength))
  ) {
    return null;
  }

  const bytes: number[] = [];

  for (let offset = 0; offset < encodedValue.length; offset += 4) {
    const quartet = encodedValue.slice(offset, offset + 4);
    const isFinalQuartet = offset + 4 === encodedValue.length;
    const values = quartet.split("").map((character, index) => {
      if (character === LEGACY_PADDING_CHARACTER) {
        return isFinalQuartet && index >= 2 ? null : Number.NaN;
      }

      return alphabet.indexOf(character);
    });

    if (
      values.some(
        (value) =>
          Number.isNaN(value) || (typeof value === "number" && value < 0)
      ) ||
      values[0] === null ||
      values[1] === null ||
      (values[2] === null && values[3] !== null)
    ) {
      return null;
    }

    const [first, second, third, fourth] = values as Array<number | null>;
    bytes.push((first! << 2) | (second! >> 4));

    if (third !== null) {
      bytes.push(((second! & 0x0f) << 4) | (third >> 2));
    }

    if (third !== null && fourth !== null) {
      bytes.push(((third & 0x03) << 6) | fourth);
    }
  }

  const decodedValue = String.fromCharCode(
    ...bytes.map((byte) => byteAliases?.get(byte) ?? byte)
  ).trim();
  return decodedValue && /^[\x20-\x7e]+$/.test(decodedValue)
    ? decodedValue
    : null;
};
